/**
 * Create workflow — /settings/custom-modules/$key/workflows/new
 *
 * Thin wrapper around <WorkflowEditor>. On submit, calls
 * modules.custom.workflows.create and navigates to the edit route.
 */

import type { FieldDefinition } from '@cloud/custom-modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  type EntitySummary,
  WorkflowEditor,
  type WorkflowFormValue,
} from '@/components/custom/workflow-editor'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/workflows/new')({
  component: NewWorkflowPage,
})

function NewWorkflowPage() {
  const { key: moduleKey } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: entitiesRaw } = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey } }),
  )
  const entities: EntitySummary[] = (entitiesRaw ?? []).map((e) => ({
    key: e.key,
    name: e.name,
    namePlural: e.namePlural,
    fields: e.fields as FieldDefinition[],
  }))

  // P2.2: pull the list of registered actions from the backend so
  // the step-type dropdown is dynamic. Cached infinitely — the
  // registry doesn't change without a backend restart.
  const { data: availableActions = [] } = useQuery(
    orpc.modules.custom.workflows.listActions.queryOptions(),
  )

  const [key, setKey] = useState('')
  const [keyDirty, setKeyDirty] = useState(false)
  const [form, setForm] = useState<WorkflowFormValue>({
    name: '',
    description: '',
    enabled: true,
    trigger: { type: 'record.created' },
    steps: [],
  })

  const createMutation = useMutation(
    orpc.modules.custom.workflows.create.mutationOptions({
      onSuccess: (wf) => {
        toast.success(`Workflow "${wf.name}" created`)
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.workflows.list.queryKey({
            input: { moduleKey },
          }),
        })
        void navigate({
          to: '/settings/custom-modules/$key/workflows/$workflowKey',
          params: { key: moduleKey, workflowKey: wf.key },
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  // Functional setForm — two paths write to `form` on this page (the
  // Name input in `keyField` and the trigger/steps editors inside
  // WorkflowEditor). Without the updater form, React's async state
  // updates would let one path overwrite the other with a stale form
  // snapshot — e.g., picking an entity and then typing in the Name
  // field would wipe the entityKey.
  const handleNameChange = (value: string) => {
    setForm((prev) => ({ ...prev, name: value }))
    if (!keyDirty) {
      setKey(
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, ''),
      )
    }
  }

  // NOTE: intentionally no <form> wrapper. See $workflowKey.tsx for the
  // explanation — Radix Select's form-control branch bubbles a native
  // <select> that wipes controlled state on mount when options haven't
  // registered yet.
  const handleSubmit = () => {
    createMutation.mutate({
      moduleKey,
      key,
      name: form.name,
      description: form.description || undefined,
      enabled: form.enabled,
      trigger: form.trigger,
      steps: form.steps,
    })
  }

  return (
    <div className="space-y-4">
      <Link
        to="/settings/custom-modules/$key/workflows"
        params={{ key: moduleKey }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to workflows
      </Link>

      <div className="space-y-4">
        <WorkflowEditor
          value={form}
          onChange={setForm}
          entities={entities}
          availableActions={availableActions}
          keyField={
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <Label>Key</Label>
                  <Input
                    value={key}
                    onChange={(e) => {
                      setKey(e.target.value)
                      setKeyDirty(true)
                    }}
                    placeholder="notify_on_fail"
                    pattern="[a-z][a-z0-9_]*"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Unique within this module. Used in logs and audit.
                  </p>
                </div>
                <div>
                  <Label>Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Notify QA on low scores"
                    required
                  />
                </div>
              </CardContent>
            </Card>
          }
        />

        <div className="flex justify-end gap-2">
          <Link
            to="/settings/custom-modules/$key/workflows"
            params={{ key: moduleKey }}
          >
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Link>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create workflow'}
          </Button>
        </div>
      </div>
    </div>
  )
}
